/* Company cell for Packing List / Bulk Data history — red "Company missing" chip opens a picker. */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { API, getAuthHeader } from "@/App";

export const DocCompanyCell = ({ entry, type, onSaved, className = "" }) => {
  const name = entry.sender_company || entry.header?.sender_company || "";
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState("");
  const tid = `${type}-company-${entry.id}`;

  if (name) return <td className={className} data-testid={tid}>{name}</td>;

  const load = async (o) => {
    setOpen(o);
    if (!o || list) return;
    try { setList((await axios.get(`${API}/companies`, { headers: getAuthHeader() })).data || []); }
    catch { setList([]); toast.error("Could not load companies"); }
  };
  const pick = async (c) => {
    setBusy(c.id);
    try {
      const r = await axios.post(`${API}/packing-list/doc-company`, { type, id: entry.id, company_id: c.id }, { headers: getAuthHeader() });
      toast.success(`Company set: ${r.data.sender_company}${r.data.siblings_updated ? ` (+${r.data.siblings_updated} same-invoice doc)` : ""}`);
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save company"); } finally { setBusy(""); }
  };

  return (
    <td className={className} data-testid={tid}>
      <Popover open={open} onOpenChange={load}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={`${tid}-missing`}
            className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700 hover:bg-rose-100 transition-colors">
            <AlertTriangle className="w-3 h-3" /> Company missing
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-1" data-testid={`${tid}-picker`}>
          <div className="px-2 py-1.5 text-[11px] font-semibold text-slate-500">Pick sender company — invoice {entry.invoice_no || "—"}</div>
          {list === null ? (
            <div className="flex items-center gap-2 px-2 py-2 text-xs text-slate-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
          ) : !list.length ? (
            <div className="px-2 py-2 text-xs text-slate-500">No companies — add one in Manage Companies</div>
          ) : list.map(c => (
            <button key={c.id} type="button" disabled={!!busy} onClick={() => pick(c)} data-testid={`${tid}-pick-${c.id}`}
              className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-indigo-50 disabled:opacity-60 transition-colors">
              <span className="font-medium text-slate-800 truncate">{c.name}</span>
              {busy === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span className="font-mono text-[10px] text-slate-400">{c.gstin || c.iec || ""}</span>}
            </button>
          ))}
        </PopoverContent>
      </Popover>
    </td>
  );
};
